import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Text,
  Box,
  InlineGrid,
  BlockStack,
  Button,
} from '@shopify/polaris';
import {
  ChartVerticalIcon,
  CashDollarIcon,
  ReceiptIcon,
  PersonIcon,
  ProductIcon,
  OrderIcon,
} from '@shopify/polaris-icons';

interface ReportCard {
  title: string;
  description: string;
  path: string;
  icon: React.ComponentType;
}

const REPORT_CARDS: ReportCard[] = [
  {
    title: 'Sales Summary',
    description: 'Overview of total orders, revenue, and average order value broken down by type.',
    path: '/reports/sales-summary',
    icon: ChartVerticalIcon,
  },
  {
    title: 'Clinic Revenue',
    description: 'Revenue breakdown per clinic including outstanding balances and order counts.',
    path: '/reports/clinic-revenue',
    icon: PersonIcon,
  },
  {
    title: 'Agent Commission',
    description: 'Commission totals per sales agent with pending and paid breakdowns.',
    path: '/reports/agent-commission',
    icon: CashDollarIcon,
  },
  {
    title: 'Aged Receivables',
    description: 'Outstanding invoices grouped by aging buckets to track overdue accounts.',
    path: '/reports/aged-receivables',
    icon: ReceiptIcon,
  },
  {
    title: 'Inventory Usage',
    description: 'Asset utilization rates, availability, and dispatch counts per SKU.',
    path: '/reports/inventory-usage',
    icon: ProductIcon,
  },
  {
    title: 'Consignment',
    description: 'Consignment order details with clinic, patient, duration, and current status.',
    path: '/reports/consignment',
    icon: OrderIcon,
  },
  {
    title: 'Overdue Rentals',
    description: 'All overdue rentals filterable by type — direct, clinic, demo, and consignment.',
    path: '/reports/overdue',
    icon: ReceiptIcon,
  },
  {
    title: 'Fulfilled by Clinic',
    description: 'Orders fulfilled from clinic consignment stock with clinic-level breakdowns.',
    path: '/reports/fulfilled-by-clinic',
    icon: PersonIcon,
  },
  {
    title: 'Patient',
    description: 'Rental history per patient including total rentals, revenue, and recent orders.',
    path: '/reports/patient',
    icon: PersonIcon,
  },
  {
    title: 'Serial Number',
    description: 'Asset lifecycle summary with transaction history and current status per serial.',
    path: '/reports/serial',
    icon: ProductIcon,
  },
  {
    title: 'Product',
    description: 'Rental volume and revenue per product SKU with bilateral and duration breakdowns.',
    path: '/reports/product',
    icon: ProductIcon,
  },
  {
    title: 'All Orders Billing',
    description: 'One row per order with subtotal, tax, shipping, total, and clinic invoice status.',
    path: '/reports/billing',
    icon: ReceiptIcon,
  },
  {
    title: 'Missing Send Dates',
    description: 'Orders at risk of missing dispatch — start date passed or imminent with no shipment.',
    path: '/reports/missing-send-dates',
    icon: OrderIcon,
  },
  {
    title: 'Sent Orders',
    description: 'Orders dispatched in a date range with ship date, carrier, and tracking.',
    path: '/reports/sent-orders',
    icon: OrderIcon,
  },
];

export default function ReportsIndex() {
  const navigate = useNavigate();

  return (
    <Page title="Reports">
      <InlineGrid columns={3} gap="400">
        {REPORT_CARDS.map((report) => (
          <Card key={report.path}>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                {report.title}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {report.description}
              </Text>
              <Box paddingBlockStart="200">
                <Button onClick={() => navigate(report.path)}>View Report</Button>
              </Box>
            </BlockStack>
          </Card>
        ))}
      </InlineGrid>
    </Page>
  );
}
